<template>
  <div
    class="sections"
    :class="{ horizontal: props.horizontal }">
    <div class="section type">
      <div class="options">
        <GroupingModes
          :are-grouping-modes-floating="false"
          :grouping-modes="profileOptionsRef"
          :show-section-separator-line="false"
          :margins="{ top: props.horizontal ? 0 : 1.5, bottom: props.horizontal ? 0 : 1.5 }"
          :small="true"
          :uppercase="false"
          :disabled="isLoading"
          padding="0.5 2rem"
          font-size="1.4rem"
          @mode-changed="onProfileClick" />
      </div>
    </div>

    <div class="section contour">
      <div class="options">
        <div
          v-if="!isSliderValueValid"
          class="alert alert-warning icon-xs">
          <IconWarning />
          {{ $t('messages.dynamicGeofencesMinMaxWarning', [sliderMinValue, sliderMaxValue, sliderUnit]) }}
        </div>

        <div
          class="radius-distance-control"
          :class="{ horizontal: props.horizontal }">
          <div class="text-and-unit">
            <SimpleDynamicGeofence />
            <input
              v-model="sliderValue"
              type="number"
              class="form-control"
              :disabled="isLoading"
              @input="debounceInput" />
            <TabsSlider
              :selected-tab-i-d="params.contour"
              :tabs="contourTabs"
              :is-disabled="isLoading"
              width="30rem"
              @selected-tab-changed="onContourClick" />
          </div>
          <div class="slider">
            <VueSlider
              v-model="sliderValue"
              :min="sliderMinValue"
              :max="sliderMaxValue"
              :piecewise="false"
              :piecewise-label="false"
              :process-style="{ background: 'transparent' }"
              :height="8"
              :disabled="isLoading"
              @click="onSliderValueChange"
              @drag-end="onSliderValueChange" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import geojsonExtent from '@mapbox/geojson-extent';
  import mapboxgl, { LngLatBoundsLike } from 'mapbox-gl';
  import { capitalize, computed, onMounted, reactive, ref, watch } from 'vue';
  import VueSlider from 'vue-slider-component';

  import DynamicGeofencesUtils, {
    DynamicGeofencesContourOption,
    DynamicGeofencesProfileOption,
  } from '@/common/components/controls/dynamic-geofences/DynamicGeofences.utils';
  import DynamicGeofencesContourType from '@/common/components/controls/dynamic-geofences/enums/DynamicGeofencesContourType.enum';
  import DynamicGeofencesEvent from '@/common/components/controls/dynamic-geofences/enums/DynamicGeofencesEvent.enum';
  import SimpleDynamicGeofence from '@/common/components/controls/dynamic-geofences/icons/SimpleDynamicGeofence.vue';
  import IsochroneApi, { IsochroneResponse } from '@/common/components/controls/dynamic-geofences/IsochroneApi';
  import DynamicGeofencesParams from '@/common/components/controls/dynamic-geofences/models/DynamicGeofencesParams.model';
  import GroupingModes from '@/common/components/controls/GroupingModes.vue';
  import TabsSlider from '@/common/components/controls/TabsSlider.vue';
  import { IconWarning } from '@/common/icons/monochrome';
  import Geofence from '@/common/models/geo/Geofence';
  import { getContainerHeight, getContainerWidth } from '@/globals';
  import UserNotifications from '@/utils/UserNotifications';
  import i18n from '@/vueI18n';

  interface Props {
    geofences: Geofence[];
    mapInstance?: mapboxgl.Map;
    horizontal: boolean;
    isHandlingDrawing: boolean;
  }

  interface Tab {
    id: DynamicGeofencesContourType;
    name: string;
  }

  const DEBOUNCE_TIMER = 1000;
  let debounceTimeout: ReturnType<typeof setTimeout> | null = null;
  const profileOptions: DynamicGeofencesProfileOption[] =
    DynamicGeofencesUtils.defaultDynamicGeofencesProfileOptions.map((option) => ({
      ...option,
      name: capitalize(i18n.global.t(option.name)),
    }));
  const contourOptions: DynamicGeofencesContourOption[] = DynamicGeofencesUtils.defaultDynamicGeofencesContourOptions;
  const contourTabs: Tab[] = contourOptions.map((option) => ({
    id: option.contour,
    name: `${capitalize(i18n.global.t(option.name))} (${i18n.global.t(option.unitName)})`,
  })); // TODO make km option available https://hivestack.atlassian.net/browse/CAM-4984
  const mapContainerId = '.map-container';
  const defaultTimeValue = 1;
  const defaultDistanceValue = 1000;
  const defaultParams: DynamicGeofencesParams = {
    profile: profileOptions[0].profile,
    contour: contourOptions[0].contour,
    options: defaultTimeValue,
    coordinates: null,
  };

  const emit = defineEmits([
    DynamicGeofencesEvent.POLYGON_CHANGED,
    DynamicGeofencesEvent.POLYGON_LOADING,
    DynamicGeofencesEvent.POLYGON_CLICKED,
    DynamicGeofencesEvent.GEOFENCE_UPDATED,
    DynamicGeofencesEvent.POLYGON_LOADING,
  ]);

  const isochroneApi = new IsochroneApi();

  const props = defineProps<Props>();
  const params = reactive<DynamicGeofencesParams>({ ...defaultParams });
  const isochronesResult = ref<IsochroneResponse[]>([]);
  const sliderValue = ref<number>(0);
  const profileOptionsRef = ref<DynamicGeofencesProfileOption[]>(profileOptions);
  const isLoading = ref<boolean>(false);

  const sliderMinValue = computed<number>(() => DynamicGeofencesUtils.contourMinMaxValues[params.contour]?.min || 0);
  const sliderMaxValue = computed<number>(() => DynamicGeofencesUtils.contourMinMaxValues[params.contour]?.max || 0);
  const sliderUnit = computed<string>(() => DynamicGeofencesUtils.contourMinMaxValues[params.contour]?.unit || '');
  const isSliderValueValid = computed<boolean>(
    () => sliderValue.value >= sliderMinValue.value && sliderValue.value <= sliderMaxValue.value,
  );
  const bbox = computed<LngLatBoundsLike>(() =>
    geojsonExtent({
      type: 'FeatureCollection',
      features: isochronesResult.value.map((iso) => iso.data.features[0]),
    }),
  );

  function fitToBounds(): void {
    if (props.mapInstance) {
      const marginH = getContainerWidth(mapContainerId) / 5;
      const marginV = getContainerHeight(mapContainerId) / 5;

      props.mapInstance.fitBounds(bbox.value, {
        padding: {
          left: marginH,
          right: marginH,
          top: marginV,
          bottom: marginV,
        },
        duration: 500,
      });
    }
  }

  function clearDynamicGeofencesLayers(): void {
    const { layers } = props.mapInstance.getStyle();
    const isochronesLayers = layers.filter((layer) => layer.id.includes('isochrone'));
    isochronesLayers.forEach((layer) => {
      DynamicGeofencesUtils.removeLayer(props.mapInstance, layer.id);
    });
  }

  function onPolygonsChanged(isochroneResponses: IsochroneResponse[], geofences: Geofence[]): void {
    isochroneResponses.forEach((iso) => {
      const { geofenceId, data } = iso;
      const geofence = geofences.find((g) => g.id === geofenceId);

      if (geofence) {
        const updatedGeofence: Geofence = Geofence.fromObject(
          {
            ...geofence,
            dynamicGeofences: { params, featureCollection: data },
            geoJson: data.features[0].geometry,
          },
          null,
        );

        emit(DynamicGeofencesEvent.GEOFENCE_UPDATED, updatedGeofence);

        if (props.isHandlingDrawing) {
          DynamicGeofencesUtils.drawOnTheMap(props.mapInstance, updatedGeofence);
        }
      }
    });

    setTimeout(() => {
      fitToBounds();
    }, 500);
  }

  /**
   * Pass the params when using getIsochrones from outside the component to get the data for more geofences than passed
   * to the component. Useful if you want to apply the coordinates to all geofences
   */
  async function getIsochrones(geofences?: Geofence[]): Promise<void> {
    if (!params.options || !isSliderValueValid.value) return;

    const promises = [];
    const geo: Geofence[] = geofences || props.geofences;

    geo.forEach((geofence) => {
      promises.push(
        isochroneApi.getIsochrones({
          ...params,
          coordinates: [geofence.lng, geofence.lat],
          geofenceId: geofence.id,
        } as DynamicGeofencesParams),
      );
    });

    if (promises.length === 0) return;

    isLoading.value = true;
    emit(DynamicGeofencesEvent.POLYGON_LOADING, true);
    try {
      const res: IsochroneResponse[] = await Promise.all(promises);
      isochronesResult.value = res;

      // If a map instance has been passed to the component, handle everything in-house,
      // otherwise, emit the result to the parent component
      if (props.mapInstance) {
        onPolygonsChanged(res, geo);
      } else {
        emit(DynamicGeofencesEvent.POLYGON_CHANGED, res);
      }

      UserNotifications.success({
        title: capitalize(i18n.global.t('names.success', 1)),
        message: capitalize(i18n.global.t('messages.successfullyGeneratedGeofences', 1)),
      });
    } catch (e) {
      // 429 Too many requests
      // Inform the user they made too many requests and they should either wait or target less POIs
      if ((e as { response: { status: number } })?.response?.status === 429) {
        UserNotifications.error({
          title: i18n.global.t('messages.tooManyRequests'),
          message: i18n.global.t('messages.isochroneApiLimitReached'),
          details: {
            label: i18n.global.t('messages.viewErrorReport'),
            modalTitle: capitalize(i18n.global.t('names.errorReport')),
            content: JSON.stringify(e, null, 2),
          },
        });
      } else {
        UserNotifications.somethingWentWrong(e);
        throw e;
      }
    } finally {
      isLoading.value = false;
      emit(DynamicGeofencesEvent.POLYGON_LOADING, false);
    }
  }

  function setParamsOptions(value: number): void {
    params.options = value;
    getIsochrones();
  }

  function onSliderValueChange(): void {
    if (sliderValue.value !== params.options) {
      setParamsOptions(sliderValue.value);
    }
  }

  function onProfileClick(option: DynamicGeofencesProfileOption): void {
    if (params.profile !== option.profile) {
      params.profile = option.profile;
      getIsochrones();
    }
  }

  function onContourClick(option: Tab): void {
    params.contour = option.id;
    // if we change the contour type, we need to reset the slider value to prevent having a value that is outside of min and max range
    // use the default value
    sliderValue.value =
      params.contour === DynamicGeofencesContourType.Distance ? defaultDistanceValue : defaultTimeValue;
    onSliderValueChange();
  }

  function debounceInput(): void {
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(() => {
      setParamsOptions(sliderValue.value);
    }, DEBOUNCE_TIMER);
  }
  function reapplyDynamicGeofencesParams(geofence: Geofence): void {
    const { dynamicGeofences } = geofence;
    profileOptionsRef.value = profileOptions.map((option) => ({
      ...option,
      selected: option.profile === dynamicGeofences.params.profile,
    }));

    params.profile = dynamicGeofences.params.profile;
    params.contour = dynamicGeofences.params.contour;
    sliderValue.value = dynamicGeofences.params.options;
    params.options = sliderValue.value;
  }

  async function applyToAllGeofences(geofences: Geofence[]): Promise<void> {
    clearDynamicGeofencesLayers();
    const updatedGeofences = [...geofences].map((g) =>
      Geofence.fromObject(
        {
          ...g,
          dynamicGeofences: { params, featureCollection: null },
        },
        null,
      ),
    );
    await getIsochrones(updatedGeofences);
  }

  defineExpose({
    applyToAllGeofences,
    getIsochrones,
    clearDynamicGeofencesLayers,
    isochronesResult,
    params,
  });

  watch(
    () => props.geofences,
    (newValue, oldValue) => {
      // reapply dynamic geofences params if they exist, useful when clicking on a geofence to make the geofence params popin appear
      if (
        oldValue?.length === 1 &&
        newValue?.length === 1 &&
        oldValue?.[0]?.id !== newValue?.[0]?.id &&
        newValue?.[0]?.dynamicGeofences?.params
      ) {
        reapplyDynamicGeofencesParams(newValue[0]);
      }
    },
  );

  watch(isLoading, () => {
    emit(DynamicGeofencesEvent.POLYGON_LOADING, isLoading.value);
  });

  onMounted(() => {
    // reapply dynamic geofences params if they exist, useful when clicking on a geofence to make the geofence params popin appear
    if (props.geofences.length === 1 && props.geofences[0].dynamicGeofences?.params) {
      reapplyDynamicGeofencesParams(props.geofences[0]);
    }
  });
</script>

<style lang="scss">
  .sections {
    display: flex;
    flex-direction: column;

    &.horizontal {
      flex-direction: row;
      align-items: center;

      .section {
        &.type {
          margin-right: 2rem;
          padding-right: 2rem;
          margin-bottom: 1rem;
        }

        &.contour {
          flex: 1;
        }
      }
    }

    .section {
      display: flex;
      flex-direction: column;

      .options {
        display: flex;
        flex-direction: column;
        justify-content: space-between;

        > div {
          svg {
            margin-right: 1rem;
          }
        }
      }
    }
  }

  p {
    margin-bottom: 0;
    padding-bottom: 0;
  }

  .radius-distance-control {
    display: flex;
    flex-direction: column;

    &.horizontal {
      flex-direction: row;
      align-items: center;

      .slider {
        margin-left: 1.6rem;
        flex: 1;
      }
    }

    .text-and-unit {
      display: flex;
      align-items: center;
      margin-bottom: 1rem;

      > svg {
        width: 2.4rem;
        height: 2.4rem;
        margin-right: 0.8rem;
        stroke: var(--textColor);
      }

      input {
        width: 7.5rem;
        margin-right: 1.6rem !important;
      }
    }

    .loader {
      display: flex;
      align-items: center;
      margin-bottom: 0.5rem;
      height: 30px;
      width: 30px;
    }
  }
</style>
